import type { ModuleFederationRuntimePlugin } from '@module-federation/runtime';

/**
 * Same as shell-fe's `mf-runtime-fallback.ts`.
 *
 * Module Federation runtime plugin that makes a failed remote load resolve to a
 * safe fallback module instead of rejecting.
 *
 * Without this, a fetch/parse failure for a remote's `mf-manifest.json` (e.g. the
 * remote MFE is down or its edge returns a non-JSON error page) rejects deep in
 * the federation runtime. That rejection escapes React's render tree as an
 * UNHANDLED promise rejection, which a React error boundary cannot catch — so the
 * whole page shows a runtime-error / blank screen.
 *
 * By handling `errorLoadRemote` we make `loadRemote` resolve with a component
 * that throws *during render*. A throw in render IS catchable by the surrounding
 * RemoteBoundary, so the failed section shows its boundary fallback while the
 * rest of the page keeps working.
 *
 * IMPORTANT: this module executes as part of the federation bootstrap, before
 * shared singletons (react) are negotiated. It must NOT statically import any
 * shared/federated dependency (e.g. `react`) or it will deadlock host init.
 */
export default function remoteFallbackPlugin(): ModuleFederationRuntimePlugin {
  return {
    name: 'public-remote-fallback-plugin',
    createScript({
      url,
      attrs,
    }: {
      url: string;
      attrs?: Record<string, string | boolean | undefined>;
    }) {
      const script = document.createElement('script');
      script.src = url;
      script.crossOrigin = 'anonymous';

      if (attrs) {
        Object.entries(attrs).forEach(([key, value]) => {
          if (key !== 'crossorigin' && key !== 'type' && value !== undefined) {
            script.setAttribute(key, String(value));
          }
        });
      }

      const cleanUrl = url.split('?')[0].split('#')[0];
      if (cleanUrl.endsWith('.js') || attrs?.type === 'module') {
        script.type = 'module';
      } else if (attrs?.type) {
        script.type = String(attrs.type);
      } else {
        // Retain default classic script type
      }

      return script;
    },
    createLink({
      url,
      attrs,
    }: {
      url: string;
      attrs?: Record<string, string | boolean | undefined>;
    }) {
      const link = document.createElement('link');
      link.setAttribute('href', url);
      link.setAttribute('crossorigin', 'anonymous');
      if (attrs) {
        Object.entries(attrs).forEach(([key, value]) => {
          if (key !== 'crossorigin' && value !== undefined) {
            link.setAttribute(key, String(value));
          }
        });
      }
      return link;
    },
    errorLoadRemote(args) {
      // eslint-disable-next-line no-console
      console.warn(
        `[MF fallback] Remote "${args.id}" failed to load; deferring to error boundary.`,
        args.error,
      );

      const message =
        args.error instanceof Error ? args.error.message : String(args.error);

      // A React component that throws on render. React.lazy resolves successfully
      // (no uncaught rejection); the throw is caught by the nearest RemoteBoundary.
      const Fallback = () => {
        throw new Error(
          `Remote "${args.id}" is unavailable: [DEBUG] - ${message}`,
        );
      };

      // The vite virtual remote module unwraps `.default`; `__esModule` ensures
      // interop treats this as an ES module namespace (not a CJS object whose
      // whole body becomes `.default`).
      return { __esModule: true, default: Fallback };
    },
  };
}
